import * as THREE from 'three';
import { ParticleEmitter, EffectFactory } from './ParticleSystem';
import { EventBus } from '../core/EventBus';

export class EffectsManager {
  private factory: EffectFactory;
  private activeEffects: ParticleEmitter[] = [];

  constructor(scene: THREE.Scene) {
    this.factory = new EffectFactory(scene);
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Weapon fired - muzzle flash
    EventBus.on('weapon:fired', (data) => {
      if (data.muzzlePosition && data.direction) {
        const pos = new THREE.Vector3(
          data.muzzlePosition.x,
          data.muzzlePosition.y,
          data.muzzlePosition.z
        );
        const dir = new THREE.Vector3(
          data.direction.x,
          data.direction.y,
          data.direction.z
        );
        const color = data.weaponType === 'Chain Gun'
          ? new THREE.Color(0xff8800)
          : new THREE.Color(0x00ffff);
        this.spawnMuzzleFlash(pos, dir, color);
      }
    });

    // Projectile hit - sparks
    EventBus.on('projectile:hit', (data) => {
      const pos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
      const normal = data.normal
        ? new THREE.Vector3(data.normal.x, data.normal.y, data.normal.z)
        : new THREE.Vector3(0, 1, 0);
      this.spawnSparks(pos, normal);
    });

    // Target destroyed - explosion
    EventBus.on('target:destroyed', (data) => {
      const pos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
      this.spawnExplosion(pos);
    });
  }

  public spawnExplosion(position: THREE.Vector3, color?: THREE.Color): void {
    const effect = this.factory.createExplosion(position, color);
    this.activeEffects.push(effect);
  }

  public spawnSparks(position: THREE.Vector3, direction: THREE.Vector3): void {
    const effect = this.factory.createSparks(position, direction);
    this.activeEffects.push(effect);
  }

  public spawnMuzzleFlash(position: THREE.Vector3, direction: THREE.Vector3, color?: THREE.Color): void {
    const effect = this.factory.createMuzzleFlash(position, direction, color);
    this.activeEffects.push(effect);
  }

  public spawnPickupEffect(position: THREE.Vector3, color: THREE.Color): void {
    const effect = this.factory.createPickupEffect(position, color);
    this.activeEffects.push(effect);
  }

  public update(dt: number): void {
    // Update all active effects
    for (let i = this.activeEffects.length - 1; i >= 0; i--) {
      const effect = this.activeEffects[i];
      effect.update(dt);

      if (effect.isFinished()) {
        effect.destroy();
        this.activeEffects.splice(i, 1);
      }
    }
  }

  public destroy(): void {
    for (const effect of this.activeEffects) {
      effect.destroy();
    }
    this.activeEffects = [];
  }
}
